import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, BookOpen, Clock, Zap } from 'lucide-react';
import type { ActionSpec, Insight } from '@duty/shared';
import { SeverityBadge, type Severity } from './SeverityBadge';
import { snippetForDoc } from '@/incident/doctrine';
import { useMotionReduced } from '@/hooks/useMotionReduced';

/** Milliseconds before a card's freshness indicator shows "stale". */
const STALE_MS = 5 * 60 * 1000;

function freshnessLabel(lastEvaluatedAtMs: number): string {
  const age = Date.now() - lastEvaluatedAtMs;
  if (age < 60_000) return 'just now';
  if (age < STALE_MS) return `${Math.floor(age / 60_000)}m ago`;
  return `${Math.floor(age / 60_000)}m ago ·  stale`;
}

function freshnessOpacity(lastEvaluatedAtMs: number): number {
  const age = Date.now() - lastEvaluatedAtMs;
  // Fade toward 0.6 once data is older than STALE_MS; still legible.
  return age < STALE_MS ? 1 : Math.max(0.6, 1 - (age - STALE_MS) / (10 * 60_000));
}

/** Glanceable, live insight card with acknowledge / dismiss (+ reason) and actionable buttons. */
export function InsightCard({
  insight,
  index = 0,
  canRespond = true,
  onAcknowledge,
  onDismiss,
  onAction,
}: {
  insight: Insight;
  index?: number;
  canRespond?: boolean;
  onAcknowledge: (id: string) => void;
  onDismiss: (id: string, reason: string) => void;
  onAction?: (spec: ActionSpec) => void;
}) {
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  const acknowledged = insight.status === 'acknowledged';
  const reduced = useMotionReduced();
  const opacity = freshnessOpacity(insight.lastEvaluatedAtMs);

  const motionProps = reduced
    ? {}
    : {
        layout: true as const,
        initial: { opacity: 0, y: 8 },
        animate: { opacity: opacity, y: 0 },
        exit: { opacity: 0, scale: 0.97, transition: { duration: 0.15 } },
        transition: { duration: 0.2, delay: Math.min(index * 0.04, 0.2) },
      };

  return (
    <motion.article
      {...motionProps}
      style={reduced ? { opacity } : undefined}
      className={`panel p-3.5 ${acknowledged ? 'opacity-75' : ''}`}
      aria-label={`${insight.severity} insight: ${insight.title}`}
    >
      <header className="mb-1.5 flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold leading-tight">{insight.title}</h3>
        <div className="flex shrink-0 items-center gap-1.5">
          {acknowledged && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-ok">
              <Check size={12} aria-hidden /> Ack
            </span>
          )}
          <SeverityBadge severity={insight.severity as Severity} />
        </div>
      </header>

      <p className="mb-2 text-sm text-text/90">{insight.message}</p>

      <details className="group">
        <summary className="cursor-pointer list-none text-xs text-accent">Why shown</summary>
        <ul className="mt-1.5 space-y-1 border-l border-border pl-3 text-xs text-muted">
          {insight.whyShown.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      </details>

      {insight.doctrineRefs && insight.doctrineRefs.length > 0 && (
        <details className="group mt-1.5">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-xs text-accent">
            <BookOpen size={12} aria-hidden /> Doctrine:{' '}
            {insight.doctrineRefs.map((d) => d.title).join('; ')}
          </summary>
          <div className="mt-1.5 space-y-1.5 border-l border-accent/40 pl-3 text-xs text-muted">
            {insight.doctrineRefs.map((d) => {
              const snippet = snippetForDoc(d.docId);
              return snippet ? <p key={d.docId}>{snippet}</p> : null;
            })}
          </div>
        </details>
      )}

      {insight.suggestedActions.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {insight.suggestedActions.map((a, i) => {
            const spec = insight.actionSpecs?.[i];
            if (spec && onAction && canRespond) {
              return (
                <button
                  key={a}
                  onClick={() => onAction(spec)}
                  className="inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-2 py-0.5 text-xs text-accent hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
                  title="Take action"
                >
                  <Zap size={10} aria-hidden />
                  {a}
                </button>
              );
            }
            return (
              <span key={a} className="chip">
                {a}
              </span>
            );
          })}
        </div>
      )}

      {!canRespond ? (
        <footer className="mt-2.5 flex items-center justify-between text-[11px] text-muted">
          <ConfidenceRing confidence={insight.confidence} />
          <Freshness ms={insight.lastEvaluatedAtMs} />
        </footer>
      ) : dismissing ? (
        <form
          className="mt-2.5 flex items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            onDismiss(insight.id, reason.trim() || 'no reason given');
          }}
        >
          <input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Dismiss reason (recorded)…"
            aria-label="Dismiss reason"
            className="min-w-0 flex-1 rounded-md border border-border bg-surface-2 px-2 py-1 text-xs focus:border-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          />
          <button
            type="submit"
            className="rounded-md bg-accent/15 px-2 py-1 text-xs text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setDismissing(false)}
            className="rounded-md p-1 text-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            aria-label="Cancel dismiss"
          >
            <X size={14} aria-hidden />
          </button>
        </form>
      ) : (
        <footer className="mt-2.5 flex items-center justify-between text-[11px] text-muted">
          <ConfidenceRing confidence={insight.confidence} />
          <span className="flex items-center gap-3">
            <Freshness ms={insight.lastEvaluatedAtMs} />
            {!acknowledged && (
              <button
                onClick={() => onAcknowledge(insight.id)}
                className="hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 rounded"
              >
                Acknowledge
              </button>
            )}
            <button
              onClick={() => setDismissing(true)}
              className="hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 rounded"
            >
              Dismiss
            </button>
          </span>
        </footer>
      )}
    </motion.article>
  );
}

const CONF_RATIO: Record<Insight['confidence'], number> = { low: 0.33, medium: 0.66, high: 1 };

function ConfidenceRing({ confidence }: { confidence: Insight['confidence'] }) {
  const r = 7;
  const circ = 2 * Math.PI * r;
  const ratio = CONF_RATIO[confidence];
  return (
    <span
      className="inline-flex items-center gap-1.5"
      aria-label={`Confidence: ${confidence}`}
      title={`Confidence: ${confidence}`}
    >
      <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden>
        {/* track */}
        <circle cx={9} cy={9} r={r} fill="none" stroke="rgb(var(--c-border))" strokeWidth={2} />
        {/* filled arc */}
        <circle
          cx={9}
          cy={9}
          r={r}
          fill="none"
          stroke="rgb(var(--c-accent))"
          strokeWidth={2}
          strokeDasharray={`${circ * ratio} ${circ * (1 - ratio)}`}
          strokeLinecap="round"
          transform="rotate(-90 9 9)"
        />
      </svg>
      <span className="text-[10px]">{confidence}</span>
    </span>
  );
}

function Freshness({ ms }: { ms: number }) {
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] text-muted/70"
      title={`Last evaluated: ${new Date(ms).toLocaleTimeString()}`}
    >
      <Clock size={10} aria-hidden />
      {freshnessLabel(ms)}
    </span>
  );
}
